import * as Yup from 'yup'
import moment from 'moment'

const PERSONALIZED_PERIOD_MAX_DEPTH_MONTHS = 13
const DATE_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD']
const INVALID_DATE_FORMAT_ERROR = 'Formato non valido'
const INVALID_DATE_ERROR = 'Data non valida'
const PERSONALIZED_PERIOD_DEPTH_ERROR = 'La data non può essere precedente agli ultimi 13 mesi'
const DATE_AFTER_TODAY_ERROR = 'La data non può essere successiva a oggi'
const START_DATE_AFTER_END_DATE_ERROR = 'La data di inizio non può essere successiva alla data di fine'
const END_DATE_BEFORE_START_DATE_ERROR = 'La data di fine non può essere precedente alla data di inizio'

export const FORM_KEYS = {
  PERIOD: 'period',
  PERSONALIZED_PERIOD_FROM: 'dateFrom',
  PERSONALIZED_PERIOD_TO: 'dateTo',
  REGION: 'region',
  PROVINCE: 'province',
  TERMINAL_ID: 'terminalId',
  PRACTICAL_STATUS: 'practicalStatus',
  RESEARCH_TYPE: 'researchType',
  RESEARCH_BY_CODE: 'researchByCode',
  RESEARCH_BY_ORDER: 'orderNumber'
}

const parseDateValue = value => {
  if (!value) return null
  if (moment.isMoment(value)) return value.clone()
  if (value instanceof Date) return moment(value)
  if (typeof value === 'string') return moment(value, DATE_FORMATS, true)
  return moment(value)
}

const isEmptyDateValue = value => value === undefined || value === null || value === ''

const isPersonalizedPeriod = (FORM_KEYS, values) => values[FORM_KEYS.PERIOD] === 'PERSONALIZED'

const shouldValidatePersonalizedDate = (FORM_KEYS, values, value) =>
  isPersonalizedPeriod(FORM_KEYS, values) && !isEmptyDateValue(value)

const isSupportedDateFormat = value => {
  if (isEmptyDateValue(value)) return true
  if (moment.isMoment(value) || value instanceof Date) return true
  if (typeof value !== 'string') return false

  return /^\d{2}\/\d{2}\/\d{4}$/.test(value) || /^\d{4}-\d{2}-\d{2}$/.test(value)
}

const isValidDateValue = value => {
  const parsedDate = parseDateValue(value)
  return !parsedDate || parsedDate.isValid()
}

const isWithinPersonalizedPeriodDepth = value => {
  const parsedDate = parseDateValue(value)
  const minDate = moment()
    .subtract(PERSONALIZED_PERIOD_MAX_DEPTH_MONTHS, 'months')
    .add(1, 'day')
    .startOf('day')

  return parsedDate.clone().startOf('day').isSameOrAfter(minDate, 'day')
}

const isSameOrBeforeToday = value => parseDateValue(value).isSameOrBefore(moment(), 'day')

const isSameOrBeforeDate = (value, compareValue) =>
  parseDateValue(value).isSameOrBefore(parseDateValue(compareValue), 'day')

const isSameOrAfterDate = (value, compareValue) =>
  parseDateValue(value).isSameOrAfter(parseDateValue(compareValue), 'day')

const buildPersonalizedDateValidation = FORM_KEYS => Yup.mixed()
  .test('date-format', INVALID_DATE_FORMAT_ERROR, function (value) {
    if (!shouldValidatePersonalizedDate(FORM_KEYS, this.parent, value)) return true
    return isSupportedDateFormat(value)
  })
  .test('valid-format', INVALID_DATE_ERROR, function (value) {
    if (!shouldValidatePersonalizedDate(FORM_KEYS, this.parent, value)) return true
    return isValidDateValue(value)
  })
  .test('min-months-limit', PERSONALIZED_PERIOD_DEPTH_ERROR, function (value) {
    if (!shouldValidatePersonalizedDate(FORM_KEYS, this.parent, value) || !isValidDateValue(value)) return true
    return isWithinPersonalizedPeriodDepth(value)
  })
  .test('not-after-today', DATE_AFTER_TODAY_ERROR, function (value) {
    if (!shouldValidatePersonalizedDate(FORM_KEYS, this.parent, value) || !isValidDateValue(value)) return true
    return isSameOrBeforeToday(value)
  })

const buildPersonalizedStartDateValidation = FORM_KEYS => buildPersonalizedDateValidation(FORM_KEYS)
  .test('start-before-end', START_DATE_AFTER_END_DATE_ERROR, function (value) {
    const endDate = this.parent[FORM_KEYS.PERSONALIZED_PERIOD_TO]
    if (!shouldValidatePersonalizedDate(FORM_KEYS, this.parent, value) || !isValidDateValue(value) || !isValidDateValue(endDate) || isEmptyDateValue(endDate)) return true
    return isSameOrBeforeDate(value, endDate)
  })

const buildPersonalizedEndDateValidation = FORM_KEYS => buildPersonalizedDateValidation(FORM_KEYS)
  .test('end-after-start', END_DATE_BEFORE_START_DATE_ERROR, function (value) {
    const startDate = this.parent[FORM_KEYS.PERSONALIZED_PERIOD_FROM]
    if (!shouldValidatePersonalizedDate(FORM_KEYS, this.parent, value) || !isValidDateValue(value) || !isValidDateValue(startDate) || isEmptyDateValue(startDate)) return true
    return isSameOrAfterDate(value, startDate)
  })

export function getValidationSchema (FORM_KEYS) {
  return Yup.object().shape({
    [FORM_KEYS.PERSONALIZED_PERIOD_FROM]: buildPersonalizedStartDateValidation(FORM_KEYS),
    [FORM_KEYS.PERSONALIZED_PERIOD_TO]: buildPersonalizedEndDateValidation(FORM_KEYS),
    [FORM_KEYS.RESEARCH_BY_CODE]: Yup.string()
      .matches(/^\d*$/, 'Formato non valido'),
    [FORM_KEYS.RESEARCH_BY_ORDER]: Yup.string()
      .matches(/^\d*$/, 'Formato non valido')
  })
}

export function mapInitialFiltersToFormikValues (initialFilters, FORM_KEYS) {
  const defaults = {
    [FORM_KEYS.PERIOD]: '',
    [FORM_KEYS.PERSONALIZED_PERIOD_FROM]: '',
    [FORM_KEYS.PERSONALIZED_PERIOD_TO]: '',
    [FORM_KEYS.REGION]: '',
    [FORM_KEYS.PROVINCE]: '',
    [FORM_KEYS.TERMINAL_ID]: [],
    [FORM_KEYS.PRACTICAL_STATUS]: [],
    [FORM_KEYS.RESEARCH_TYPE]: '',
    [FORM_KEYS.RESEARCH_BY_CODE]: '',
    [FORM_KEYS.RESEARCH_BY_ORDER]: ''
  }
  return { ...defaults, ...initialFilters }
}

export function mapFormikValuesToFilteredRequestParams (values, FORM_KEYS) {
  const params = {
    pageNumber: 0,
    pageSize: 15
  }

  // Period — only send dateFrom/dateTo for PERSONALIZED; LAST_13_MONTHS is the API default and must not be sent
  if (values[FORM_KEYS.PERIOD] === 'PERSONALIZED') {
    if (values[FORM_KEYS.PERSONALIZED_PERIOD_FROM]) params.dateFrom = values[FORM_KEYS.PERSONALIZED_PERIOD_FROM]
    if (values[FORM_KEYS.PERSONALIZED_PERIOD_TO]) params.dateTo = values[FORM_KEYS.PERSONALIZED_PERIOD_TO]
  }

  // Terminal ID (single selection)
  if (values[FORM_KEYS.TERMINAL_ID]?.length > 0) {
    params.terminalId = values[FORM_KEYS.TERMINAL_ID][0]
  }

  // Region
  if (values[FORM_KEYS.REGION]) {
    params.region = values[FORM_KEYS.REGION]
  }

  // Province
  if (values[FORM_KEYS.PROVINCE]) {
    params.province = values[FORM_KEYS.PROVINCE]
  }

  // Practical status
  if (values[FORM_KEYS.PRACTICAL_STATUS]?.length > 0) {
    params.subStatus = values[FORM_KEYS.PRACTICAL_STATUS].join(',')
  }

  // Research: only the active field is sent
  if (values[FORM_KEYS.RESEARCH_TYPE] === 'RESEARCH_BY_CODE' && values[FORM_KEYS.RESEARCH_BY_CODE]) {
    params.dossierNumber = values[FORM_KEYS.RESEARCH_BY_CODE]
  } else if (values[FORM_KEYS.RESEARCH_TYPE] === 'RESEARCH_BY_ORDER' && values[FORM_KEYS.RESEARCH_BY_ORDER]) {
    params.orderNumber = values[FORM_KEYS.RESEARCH_BY_ORDER]
  }

  return params
}

const mapProvinceToOption = province => ({
  label: province.name,
  value: province.provinceCode
})

export function getProvinceOptionsByRegion (regionCode, provinceOptions = [], regionProvinceOptions = []) {
  if (!regionCode) return provinceOptions
  if (regionProvinceOptions.length === 0) return provinceOptions

  const selectedRegion = regionProvinceOptions.find(region => region.code === regionCode)
  if (!selectedRegion || !Array.isArray(selectedRegion.provinces)) return []

  return selectedRegion.provinces.map(mapProvinceToOption)
}

export function normalizeProvinceForRegion (regionCode, provinceCode, provinceOptions = [], regionProvinceOptions = []) {
  if (!provinceCode) return ''

  const availableProvinceOptions = getProvinceOptionsByRegion(regionCode, provinceOptions, regionProvinceOptions)
  return availableProvinceOptions.some(province => province.value === provinceCode) ? provinceCode : ''
}
